import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import cartService from '../../../services/cart';
import sessionStorageService from '../../../services/sessionStorage';

import styles from './AutoFillShippingInfoStep.module.scss';

import Spinner, {
  THEME_BLOCK_PURPLE,
} from '../../../components/common/Spinner';

import ShippingInfoStep from '../ShippingInfoStep';

import { isSameAddress } from '../../../utils';

import AddressValidatorModal from '../../../components/AddressValidatorModal';

import {
  setNeedsToCheckPaypalShippingAddressDraft,
  setNeedsToCheckAmazonPayShippingAddressDraft,
  setNeedsToCheckApplePayShippingAddressDraft,
  setNeedsToCheckCustomerAccountsShippingAddressDraft,
  setNeedsToValidateFieldsOfCustomerAccountAddressDraft,
} from '../../../slices/shippingAddress';
import usePrevious from '../../../hooks/usePrevious';
import useQueryParams from '../../../hooks/useQueryParams';

import { NO_CHANGES_VALUE, VALID_ADDRESS_SCORE } from './constants';

const AUTO_FILL_STATUS = {
  autoFillNotStarted: 'autoFillNotStarted',
  autoFillStarted: 'autoFillStarted',
  autoFillInAddressValidation: 'autoFillInAddressValidation',
  autoFillWasDone: 'autoFillWasDone',
};

const AUTO_FILL_SECTIONS = {
  spinner: 'spinner',
  avsModal: 'avsModal',
  manualShippingInfoStep: 'manualShippingInfoStep',
};

const AutoFillShippingInfoStep = ({
  showCompleted,
  isCompleted,
  showEditing,
  onClickSave,
  onClickCancel,
  shippingAddress,
  isEditing,
}) => {
  const {
    cartId,
    isCartLoading,
    customerId,
    customerAddress,
    accountsStatus,

    customerAddressDraftFromRedux,
    paypalShippingAddressDraftFromRedux,
    needsToCheckPaypalShippingAddressDraft,
    amazonPayShippingAddressDraftFromRedux,
    needsToCheckAmazonPayShippingAddressDraft,
    applePayShippingAddressDraftFromRedux,
    needsToCheckApplePayShippingAddressDraft,
    needsToCheckCustomerAccountsShippingAddressDraft,
    cartShippingAddress,
  } = useSelector((state) => ({
    cartId: state.cart.id,
    isCartLoading: state.cart.status === 'loading',
    customerId: state.accounts?.customerId,
    customerAddress: state.accounts?.customerAddress,
    accountsStatus: state.accounts?.status,

    customerAddressDraftFromRedux: state.accounts?.customerAddress,
    paypalShippingAddressDraftFromRedux:
      state.shippingAddress?.paypalShippingAddressDraft,
    needsToCheckPaypalShippingAddressDraft:
      state.shippingAddress?.needsToCheckPaypalShippingAddressDraft,
    amazonPayShippingAddressDraftFromRedux:
      state.shippingAddress?.amazonPayShippingAddressDraft,
    needsToCheckAmazonPayShippingAddressDraft:
      state.shippingAddress?.needsToCheckAmazonPayShippingAddressDraft,
    applePayShippingAddressDraftFromRedux:
      state.shippingAddress?.applePayShippingAddressDraft,
    needsToCheckApplePayShippingAddressDraft:
      state.shippingAddress?.needsToCheckApplePayShippingAddressDraft,
    needsToCheckCustomerAccountsShippingAddressDraft:
      state.shippingAddress?.needsToCheckCustomerAccountsShippingAddressDraft,
    cartShippingAddress: state.cart?.shippingAddress,
  }));

  const dispatch = useDispatch();

  const cartShippingAddressLoaded = !isCartLoading;

  const [
    allShippingAddressDraftsWhereLoadedForTheFirstTime,
    setAllShippingAddressDraftsWhereLoadedForTheFirstTime,
  ] = useState(false);

  const [
    customerAccountShippingAddressDraft,
    setCustomerAccountShippingAddressDraft,
  ] = useState();

  const [paypalShippingAddressDraft, setPaypalShippingAddressDraft] =
    useState();

  const [amazonPayShippingAddressDraft, setAmazonPayShippingAddressDraft] =
    useState();

  const [applePayShippingAddressDraft, setApplePayShippingAddressDraft] =
    useState();

  const [
    customerAccountShippingAddressDraftLoaded,
    setCustomerAccountShippingAddressDraftLoaded,
  ] = useState(false);

  const [
    paypalShippingAddressDraftLoaded,
    setPaypalShippingAddressDraftLoaded,
  ] = useState(false);

  const [
    amazonPayShippingAddressDraftLoaded,
    setAmazonPayShippingAddressDraftLoaded,
  ] = useState(false);

  const [
    applePayShippingAddressDraftLoaded,
    setApplePayShippingAddressDraftLoaded,
  ] = useState(false);

  const [avsAddressToValidate, setAvsAddressToValidate] = useState();
  const [avsVerifiedAddress, setAvsVerifiedAddress] = useState();

  const [autoFillStatus, setAutoFillStatus] = useState(
    AUTO_FILL_STATUS.autoFillNotStarted,
  );

  const [autoFillSectionToShow, setAutoFillSectionToShow] = useState(
    AUTO_FILL_SECTIONS.spinner,
  );

  const [
    shippingAddressToSendToManualStep,
    setShippingAddressToSendToManualStep,
  ] = useState(shippingAddress);

  const isLoggedIn = customerId || customerAddress?.email;

  const previousIsLoggedIn = usePrevious(isLoggedIn);

  const autoFillNotStarted =
    autoFillStatus === AUTO_FILL_STATUS.autoFillNotStarted;

  // Leaving this variable besides is not used yet
  // eslint-disable-next-line no-unused-vars
  const autoFillStarted = autoFillStatus === AUTO_FILL_STATUS.autoFillStarted;

  const autoFillInAddressValidation =
    autoFillStatus === AUTO_FILL_STATUS.autoFillInAddressValidation;

  const autoFillWasDone = autoFillStatus === AUTO_FILL_STATUS.autoFillWasDone;

  const isAccountsLoading = accountsStatus === 'loading';


  const expressPaymentAddress = localStorage.getItem('expressShippingAddress');
  
  const userChangedFromAnonymousToLoggedIn = isLoggedIn && !previousIsLoggedIn;

  const allShippingAddressDraftsAreLoaded =
    cartShippingAddressLoaded &&
    paypalShippingAddressDraftLoaded &&
    customerAccountShippingAddressDraftLoaded &&
    amazonPayShippingAddressDraftLoaded &&
    applePayShippingAddressDraftLoaded;

  const [amazonPayApproved] = useQueryParams(['amazonPayApproved']);

  useEffect(() => {
    if (
      allShippingAddressDraftsAreLoaded &&
      !allShippingAddressDraftsWhereLoadedForTheFirstTime
    ) {
      setAllShippingAddressDraftsWhereLoadedForTheFirstTime(true);
    }
  }, [
    allShippingAddressDraftsAreLoaded,
    allShippingAddressDraftsWhereLoadedForTheFirstTime,
  ]);

  // Shipping Address Draft from Amazon Pay
  useEffect(() => {
    if (
      amazonPayShippingAddressDraftLoaded &&
      needsToCheckAmazonPayShippingAddressDraft &&
      amazonPayShippingAddressDraftFromRedux
    ) {
      setAmazonPayShippingAddressDraftLoaded(false);
    }
  }, [
    amazonPayShippingAddressDraftFromRedux,
    amazonPayShippingAddressDraftLoaded,
    needsToCheckAmazonPayShippingAddressDraft,
  ]);

  // Shipping Address Draft from Amazon Pay
  useEffect(() => {
    if (!amazonPayShippingAddressDraftLoaded) {
      if (
        needsToCheckAmazonPayShippingAddressDraft &&
        amazonPayShippingAddressDraftFromRedux
      ) {
        setAmazonPayShippingAddressDraft(
          amazonPayShippingAddressDraftFromRedux,
        );
        dispatch(setNeedsToCheckAmazonPayShippingAddressDraft(false));
      }

      setAmazonPayShippingAddressDraftLoaded(true);
    }
  }, [
    amazonPayShippingAddressDraftFromRedux,
    amazonPayShippingAddressDraftLoaded,
    dispatch,
    needsToCheckAmazonPayShippingAddressDraft,
  ]);

  // Shipping Address Draft from Paypal
  useEffect(() => {
    if (
      paypalShippingAddressDraftLoaded &&
      needsToCheckPaypalShippingAddressDraft &&
      paypalShippingAddressDraftFromRedux
    ) {
      setPaypalShippingAddressDraftLoaded(false);
    }
  }, [
    needsToCheckPaypalShippingAddressDraft,
    paypalShippingAddressDraftFromRedux,
    paypalShippingAddressDraftLoaded,
  ]);

  useEffect(() => {
    if (!paypalShippingAddressDraftLoaded) {
      const paypalShippingAddressDraftValueFromSessionStorage =
        sessionStorageService.getItem({
          itemName: 'paypalShippingAddressDraft',
        });

      let paypalShippingAddressDraftFromSessionStorage;
      try {
        paypalShippingAddressDraftFromSessionStorage = JSON.parse(
          paypalShippingAddressDraftValueFromSessionStorage,
        );
        // eslint-disable-next-line no-empty
      } catch (parseError) {}

      if (paypalShippingAddressDraftFromSessionStorage) {
        setPaypalShippingAddressDraft(
          paypalShippingAddressDraftFromSessionStorage,
        );
        sessionStorageService.removeItem({
          itemName: 'paypalShippingAddressDraft',
        });
      } else if (
        needsToCheckPaypalShippingAddressDraft &&
        paypalShippingAddressDraftFromRedux
      ) {
        setPaypalShippingAddressDraft(paypalShippingAddressDraftFromRedux);
        dispatch(setNeedsToCheckPaypalShippingAddressDraft(false));
      }

      setPaypalShippingAddressDraftLoaded(true);
    }
  }, [
    dispatch,
    needsToCheckPaypalShippingAddressDraft,
    paypalShippingAddressDraftFromRedux,
    paypalShippingAddressDraftLoaded,
  ]);

  // Shipping Address from Customer Account
  useEffect(() => {
    if (
      cartShippingAddressLoaded &&
      !isAccountsLoading &&
      !customerAccountShippingAddressDraftLoaded
    ) {
      if (
        accountsStatus === 'succeeded' &&
        isLoggedIn &&
        customerAddressDraftFromRedux &&
        !expressPaymentAddress
      ) {
        setCustomerAccountShippingAddressDraft(customerAddressDraftFromRedux);
      }
      if (expressPaymentAddress) {
        setCustomerAccountShippingAddressDraft(JSON.parse(expressPaymentAddress));
      }
      setCustomerAccountShippingAddressDraftLoaded(true);
    }
  }, [
    isAccountsLoading,
    accountsStatus,
    customerAccountShippingAddressDraftLoaded,
    customerAddressDraftFromRedux,
    isLoggedIn,
    cartShippingAddress,
    dispatch,
    cartShippingAddressLoaded,
    expressPaymentAddress
  ]);

  // Shipping Address Draft from Customer Account (flag for when the user has logged in on /checkout)
  useEffect(() => {
    if (
      allShippingAddressDraftsWhereLoadedForTheFirstTime &&
      userChangedFromAnonymousToLoggedIn
    ) {
      dispatch(setNeedsToCheckCustomerAccountsShippingAddressDraft(true));
      setCustomerAccountShippingAddressDraftLoaded(false);
    }
  }, [
    allShippingAddressDraftsWhereLoadedForTheFirstTime,
    dispatch,
    userChangedFromAnonymousToLoggedIn,
  ]);

  // Shipping Address from ApplePay
  useEffect(() => {
    if (
      applePayShippingAddressDraftLoaded &&
      needsToCheckApplePayShippingAddressDraft &&
      applePayShippingAddressDraftFromRedux
    ) {
      setApplePayShippingAddressDraftLoaded(false);
    }
  }, [
    applePayShippingAddressDraftFromRedux,
    applePayShippingAddressDraftLoaded,
    needsToCheckApplePayShippingAddressDraft,
  ]);

  // Shipping Address from ApplePay
  useEffect(() => {
    if (!applePayShippingAddressDraftLoaded) {
      const applePayShippingAddressDraftValueFromSessionStorage =
        sessionStorageService.getItem({
          itemName: 'applePayShippingAddressDraft',
        });

      let applePayShippingAddressDraftFromSessionStorage;
      try {
        applePayShippingAddressDraftFromSessionStorage = JSON.parse(
          applePayShippingAddressDraftValueFromSessionStorage,
        );
        // eslint-disable-next-line no-empty
      } catch (parseError) {}

      if (applePayShippingAddressDraftFromSessionStorage) {
        setApplePayShippingAddressDraft(
          applePayShippingAddressDraftFromSessionStorage,
        );
        sessionStorageService.removeItem({
          itemName: 'applePayShippingAddressDraft',
        });
      } else if (
        needsToCheckApplePayShippingAddressDraft &&
        applePayShippingAddressDraftFromRedux
      ) {
        setApplePayShippingAddressDraft(applePayShippingAddressDraftFromRedux);
        dispatch(setNeedsToCheckApplePayShippingAddressDraft(false));
      }
      setApplePayShippingAddressDraftLoaded(true);
    }
  }, [
    applePayShippingAddressDraftFromRedux,
    applePayShippingAddressDraftLoaded,
    dispatch,
    needsToCheckApplePayShippingAddressDraft,
  ]);

  const cartShippingAddressAsJSON = JSON.stringify(cartShippingAddress);

  useEffect(() => {
    try {
      const cartShippingAddressValue = JSON.parse(cartShippingAddressAsJSON);
      setShippingAddressToSendToManualStep(cartShippingAddressValue);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [cartShippingAddressAsJSON]);

  useEffect(() => {
    let cartShippingAddressValue;
    try {
      cartShippingAddressValue = JSON.parse(cartShippingAddressAsJSON);
    } catch (error) {
      cartShippingAddressValue = undefined;
    }

    const processDraftsAndAssignShippingAddress = async () => {
      setAutoFillStatus(AUTO_FILL_STATUS.autoFillStarted);
      let shippingAddressDraftToAssign;

      // if the user just signed in, use customer address, otherwise follow this logic
      if (
        needsToCheckCustomerAccountsShippingAddressDraft &&
        customerAccountShippingAddressDraft &&
        userChangedFromAnonymousToLoggedIn
      ) {
        shippingAddressDraftToAssign = {
          ...customerAccountShippingAddressDraft,
          streetAddress: customerAccountShippingAddressDraft.streetName ?? '',
        };
        dispatch(setNeedsToCheckCustomerAccountsShippingAddressDraft(false));
        dispatch(setNeedsToValidateFieldsOfCustomerAccountAddressDraft(true));
      } else {
        if (paypalShippingAddressDraft) {
          shippingAddressDraftToAssign = {
            ...paypalShippingAddressDraft,
          };
          setPaypalShippingAddressDraft(undefined);
        } else if (amazonPayShippingAddressDraft) {
          shippingAddressDraftToAssign = {
            ...amazonPayShippingAddressDraft,
          };
          setAmazonPayShippingAddressDraft(undefined);
        } else if (applePayShippingAddressDraft) {
          shippingAddressDraftToAssign = {
            ...applePayShippingAddressDraft,
          };
          setApplePayShippingAddressDraft(undefined);
        }

        if (customerAccountShippingAddressDraft) {
          if (shippingAddressDraftToAssign) {
            shippingAddressDraftToAssign = {
              ...shippingAddressDraftToAssign,
              email: customerAccountShippingAddressDraft.email,
            };
          } else {
            shippingAddressDraftToAssign = {
              ...customerAccountShippingAddressDraft,
              streetAddress: customerAccountShippingAddressDraft.streetName,
            };
          }
          dispatch(setNeedsToCheckCustomerAccountsShippingAddressDraft(false));
        }
      }
      const isValidShippingAddressDraftToAssign =
        shippingAddressDraftToAssign?.streetAddress &&
        shippingAddressDraftToAssign?.city &&
        shippingAddressDraftToAssign?.postalCode &&
        shippingAddressDraftToAssign?.country &&
        shippingAddressDraftToAssign?.state;
      if (isValidShippingAddressDraftToAssign) {
        if (
          !cartShippingAddressValue ||
          !isSameAddress(shippingAddressDraftToAssign, cartShippingAddressValue)
        ) {
          let validateAddressResponse;
          try {
            validateAddressResponse = await cartService.validateAddress({
              ...shippingAddressDraftToAssign,
              streetAddress: shippingAddressDraftToAssign.streetAddress ?? '',
              cartId,
            });
          } catch (error) {
            setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
          }

          if (
            !validateAddressResponse.fedexChanges?.filter(
              (fedexChange) => fedexChange.text !== NO_CHANGES_VALUE,
            ).length &&
            validateAddressResponse.score === VALID_ADDRESS_SCORE
          ) {
            onClickSave(shippingAddressDraftToAssign);
            setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
          } else if (validateAddressResponse) {
            setAvsVerifiedAddress(validateAddressResponse);
            setAvsAddressToValidate(shippingAddressDraftToAssign);
            setAutoFillStatus(AUTO_FILL_STATUS.autoFillInAddressValidation);
          } else {
            setShippingAddressToSendToManualStep({
              ...shippingAddressDraftToAssign,
              streetAddress: shippingAddressDraftToAssign.streetAddress ?? '',
              streetName: shippingAddressDraftToAssign.streetAddress ?? '',
            });
            setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
          }
        } else {
          setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
        }
      } else {
        setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
      }
    };

    // If Amazon Pay payment is approved (it's returning from the 2nd interaction)
    // it doesn't assign any shippingAddress
    if (
      !amazonPayApproved &&
      autoFillNotStarted &&
      allShippingAddressDraftsAreLoaded
    ) {
      processDraftsAndAssignShippingAddress();
    }
  }, [
    cartId,
    allShippingAddressDraftsAreLoaded,
    amazonPayShippingAddressDraft,
    applePayShippingAddressDraft,
    autoFillNotStarted,
    cartShippingAddressAsJSON,
    customerAccountShippingAddressDraft,
    dispatch,
    userChangedFromAnonymousToLoggedIn,
    needsToCheckCustomerAccountsShippingAddressDraft,
    onClickSave,
    paypalShippingAddressDraft,
    amazonPayApproved,
  ]);

  useEffect(() => {
    if (
      autoFillWasDone &&
      (needsToCheckPaypalShippingAddressDraft ||
        needsToCheckCustomerAccountsShippingAddressDraft ||
        needsToCheckAmazonPayShippingAddressDraft ||
        needsToCheckApplePayShippingAddressDraft)
    ) {
      setAutoFillStatus(AUTO_FILL_STATUS.autoFillNotStarted);
    }
  }, [
    autoFillWasDone,
    needsToCheckAmazonPayShippingAddressDraft,
    needsToCheckApplePayShippingAddressDraft,
    needsToCheckCustomerAccountsShippingAddressDraft,
    needsToCheckPaypalShippingAddressDraft,
  ]);

  if (autoFillInAddressValidation) {
    if (autoFillSectionToShow !== AUTO_FILL_SECTIONS.avsModal) {
      setAutoFillSectionToShow(AUTO_FILL_SECTIONS.avsModal);
    }
  } else if (autoFillWasDone && !isCartLoading) {
    if (autoFillSectionToShow !== AUTO_FILL_SECTIONS.manualShippingInfoStep) {
      setAutoFillSectionToShow(AUTO_FILL_SECTIONS.manualShippingInfoStep);
    }
  } else if (autoFillSectionToShow !== AUTO_FILL_SECTIONS.spinner) {
    setAutoFillSectionToShow(AUTO_FILL_SECTIONS.spinner);
  }

  const handleOnSubmitAVSModal = (address) => {
    onClickSave({ ...avsAddressToValidate, ...address });
    setAutoFillStatus(AUTO_FILL_STATUS.autoFillWasDone);
  };

  const showSpinner = autoFillSectionToShow === AUTO_FILL_SECTIONS.spinner;
  const showAvsModal = autoFillSectionToShow === AUTO_FILL_SECTIONS.avsModal;
  const showManualShippingInfoStep =
    autoFillSectionToShow === AUTO_FILL_SECTIONS.manualShippingInfoStep;
  
  return (
    <>
      {(showSpinner || showAvsModal) && (
        <div className={styles['spinner-container']}>
          <Spinner isModalWrapping={false} theme={THEME_BLOCK_PURPLE} />
        </div>
      )}
      {showAvsModal && (
        <AddressValidatorModal
          firstName={avsAddressToValidate.firstName}
          lastName={avsAddressToValidate.lastName}
          verifiedAddress={avsVerifiedAddress.address}
          currentAddress={{
            streetAddress: avsAddressToValidate.streetAddress,
            city: avsAddressToValidate.city,
            postalCode: avsAddressToValidate.postalCode,
            country: avsAddressToValidate.country,
            state: avsAddressToValidate.state,
          }}
          canVerify={avsVerifiedAddress.score !== '0'}
          onSubmit={handleOnSubmitAVSModal}
          isVisible
        />
      )}
      {showManualShippingInfoStep && (
        <ShippingInfoStep
          showCompleted={showCompleted}
          isCompleted={isCompleted}
          showEditing={showEditing}
          onClickSave={onClickSave}
          onClickCancel={onClickCancel}
          shippingAddress={shippingAddressToSendToManualStep}
          isEditing={isEditing}
        />
      )}
    </>
  );
};

AutoFillShippingInfoStep.propTypes = {
  showCompleted: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool,
  showEditing: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  onClickSave: PropTypes.func.isRequired,
  onClickCancel: PropTypes.func.isRequired,
  shippingAddress: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    streetAddress: PropTypes.string,
    streetName: PropTypes.string,
    additionalStreetInfo: PropTypes.string,
    postalCode: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    state: PropTypes.string,
    id: PropTypes.string,
    isDefaultShippingAddress: PropTypes.bool,
  }).isRequired,
};

AutoFillShippingInfoStep.defaultProps = {
  isCompleted: false,
  isEditing: false,
};

export default AutoFillShippingInfoStep;
